/**
 * Copy plain text to the clipboard, with Outlook/iframe selection fallback.
 * Outlook owns the cross-origin frame and does not delegate clipboard-write.
 */
export async function copyPlainText(text: string): Promise<void> {
  // Use selection copying before awaiting anything, preserving the user's activation.
  if (window.parent !== window && copyWithSelection(text)) return
  const clipboard = navigator.clipboard
  try {
    if (!clipboard) throw new Error('Clipboard access is unavailable.')
    await clipboard.writeText(text)
  } catch (error) {
    if (!copyWithSelection(text)) throw error
  }
}

/**
 * Synchronous selection/`execCommand` copy used by Outlook iframes and as a
 * fallback when the async Clipboard API is denied.
 * Pass `html` only when the destination should receive a real HTML flavor —
 * do not advertise plain text as HTML.
 */
export function copyWithSelection(plainText: string, html?: string): boolean {
  if (typeof document.execCommand !== 'function') return false
  const focused = document.activeElement
  const selection = document.getSelection()
  const ranges = selection ? Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange()) : []
  const textarea = document.createElement('textarea')
  textarea.value = plainText
  textarea.readOnly = true
  textarea.setAttribute('aria-hidden', 'true')
  textarea.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none'
  const onCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return
    event.clipboardData.setData('text/plain', plainText)
    if (html !== undefined) {
      event.clipboardData.setData('text/html', html)
    }
    event.preventDefault()
  }
  document.body.appendChild(textarea)
  textarea.addEventListener('copy', onCopy)
  try {
    textarea.focus({ preventScroll: true })
    textarea.select()
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    textarea.removeEventListener('copy', onCopy)
    textarea.remove()
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true })
    if (selection) {
      selection.removeAllRanges()
      for (const range of ranges) selection.addRange(range)
    }
  }
}
